import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { KeyRound, ShieldCheck, Trash2, FlaskConical, Save, Eye, EyeOff, RefreshCw, ExternalLink } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { BACKEND_URL, reportError } from './adminApi';

const SOURCE_LABELS = {
  admin: { label: 'Clé admin active', className: 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]' },
  env: { label: 'Clé serveur (.env)', className: 'bg-[hsl(var(--brand-mint))] text-foreground hover:bg-[hsl(var(--brand-mint))]' },
  none: { label: 'Non configurée', className: 'bg-[hsl(var(--secondary))] text-muted-foreground hover:bg-[hsl(var(--secondary))]' },
};

const PROVIDER_LINKS = {
  google: 'https://aistudio.google.com/app/apikey',
  openai: 'https://platform.openai.com/api-keys',
  anthropic: 'https://console.anthropic.com/settings/keys',
  fal: 'https://fal.ai/dashboard/keys',
};

const MIN_KEY_LENGTH = 8;

/** Save / test / delete actions for one provider key (API calls + feedback). */
function useKeyActions(p, adminKey, onChanged) {
  const [busy, setBusy] = useState(null); // 'save' | 'test' | 'delete'
  const [testResult, setTestResult] = useState(null);
  const base = `${BACKEND_URL}/api/admin/ai-keys/${p.provider}`;

  const run = async (action, fn) => {
    setBusy(action);
    try { return await fn(); } finally { setBusy(null); }
  };

  const save = (value) => run('save', async () => {
    try {
      const res = await axios.put(`${base}?key=${adminKey}`, { key: value });
      toast.success(`${p.label} : clé enregistrée (…${res.data.last4})`);
      setTestResult(null);
      onChanged();
      return true;
    } catch (err) {
      reportError(`clé ${p.provider}`, err, () => toast.error(err?.response?.data?.detail || 'Enregistrement impossible'));
      return false;
    }
  });

  const test = (value) => run('test', async () => {
    setTestResult(null);
    try {
      const body = value ? { key: value } : undefined;
      const res = await axios.post(`${base}/test?key=${adminKey}`, body, { headers: { 'Content-Type': 'application/json' } });
      setTestResult({ ok: true, text: `${res.data.detail} (${res.data.latency_ms} ms)` });
      toast.success(`${p.label} : clé valide`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Test impossible';
      setTestResult({ ok: false, text: detail });
      reportError(`test clé ${p.provider}`, err, () => toast.error(`${p.label} : ${detail}`));
    }
  });

  const remove = () => run('delete', async () => {
    try {
      await axios.delete(`${base}?key=${adminKey}`);
      toast.success('Clé supprimée');
      setTestResult(null);
      onChanged();
    } catch (err) {
      reportError(`suppression clé ${p.provider}`, err, () => toast.error(err?.response?.data?.detail || 'Suppression impossible'));
    }
  });

  return { busy, testResult, save, test, remove };
}

function KeyRowHeader({ p }) {
  const src = SOURCE_LABELS[p.source] || SOURCE_LABELS.none;
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium flex items-center gap-1.5">
          <KeyRound className="h-4 w-4 text-[hsl(var(--primary))]" /> {p.label}
          {PROVIDER_LINKS[p.provider] && (
            <a href={PROVIDER_LINKS[p.provider]} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[hsl(var(--primary))]" aria-label="Obtenir une clé" data-testid={`ai-key-link-${p.provider}`}><ExternalLink className="h-3.5 w-3.5" /></a>
          )}
        </p>
        <p className="text-[11px] text-muted-foreground">{p.used_for}</p>
      </div>
      <Badge className={`text-[10px] ${src.className}`} data-testid={`ai-key-status-${p.provider}`}>
        {src.label}{p.last4 ? ` · ••••${p.last4}` : ''}
      </Badge>
    </div>
  );
}

function SecretInput({ provider, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative flex-1">
      <Input
        type={show ? 'text' : 'password'}
        autoComplete="off"
        className="h-9 pr-9 font-mono text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`ai-key-input-${provider}`}
      />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={show ? 'Masquer' : 'Afficher'} data-testid={`ai-key-toggle-${provider}`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function KeyRow({ p, adminKey, onChanged }) {
  const [value, setValue] = useState('');
  const { busy, testResult, save, test, remove } = useKeyActions(p, adminKey, onChanged);
  const trimmed = value.trim();
  const tooShort = trimmed.length < MIN_KEY_LENGTH;

  const onSave = async () => {
    if (tooShort) { toast.error('Clé trop courte'); return; }
    if (await save(trimmed)) setValue('');
  };
  const onRemove = () => {
    if (window.confirm(`Supprimer la clé admin « ${p.label} » ? ${p.env_var} du serveur sera utilisée si elle existe.`)) remove();
  };

  return (
    <div className="rounded-xl border border-border p-4 bg-card space-y-3" data-testid={`ai-key-row-${p.provider}`} data-source={p.source}>
      <KeyRowHeader p={p} />
      <div className="flex flex-col sm:flex-row gap-2">
        <SecretInput provider={p.provider} value={value} onChange={setValue} placeholder={p.configured ? 'Saisir une nouvelle clé pour remplacer…' : 'Coller la clé API…'} />
        <div className="flex gap-1.5">
          <Button size="sm" className="h-9" onClick={onSave} disabled={!!busy || tooShort} data-testid={`ai-key-save-${p.provider}`}>
            {busy === 'save' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Enregistrer</>}
          </Button>
          <Button size="sm" variant="outline" className="h-9" onClick={() => test(trimmed)} disabled={!!busy || (!p.configured && tooShort)} data-testid={`ai-key-test-${p.provider}`}>
            {busy === 'test' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><FlaskConical className="h-4 w-4 mr-1" /> Tester</>}
          </Button>
          {p.source === 'admin' && (
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-destructive hover:bg-destructive/10" onClick={onRemove} disabled={!!busy} aria-label="Supprimer la clé" data-testid={`ai-key-delete-${p.provider}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {testResult && (
        <p className={`text-[11px] ${testResult.ok ? 'text-[hsl(var(--primary))]' : 'text-destructive'}`} data-testid={`ai-key-test-result-${p.provider}`}>{testResult.text}</p>
      )}
    </div>
  );
}

/**
 * Admin card: manage the API keys of every AI provider (stored encrypted server-side, never echoed back).
 */
export function AdminAiKeysCard({ adminKey, onKeysChanged }) {
  const [providers, setProviders] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ai-keys?key=${adminKey}`);
      setProviders(res.data.providers || []);
    } catch (err) {
      reportError('clés API IA', err, () => toast.error('Impossible de charger les clés API'));
      setProviders([]);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onKeysChanged?.(); };

  return (
    <Card className="p-5 sm:p-6" data-testid="ai-keys-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Clés API des IA</h3>
          <p className="text-xs text-muted-foreground">Priorité : clé saisie ici &gt; clé du serveur &gt; clé universelle Emergent. Les clés sont chiffrées et jamais réaffichées en clair.</p>
        </div>
      </div>
      {providers === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-keys-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {providers.map((p) => <KeyRow key={p.provider} p={p} adminKey={adminKey} onChanged={changed} />)}
        </div>
      )}
    </Card>
  );
}
